/*----------------------------------
- COMPONENT: PUBLIC DOMAINS ROW CARD
----------------------------------*/

/**
 * @docs docs/features/website-domains-hubs
 * @adr ADR-0028
 *
 * One preview row, laid out for a phone (the mock's `phoneCard()`, website-domains.html): the glyph, the name and
 * its status, the price and the renewal on the first line; "Our read" (the rating) and every other column as
 * bands on a strip that slides sideways (`dm-bands`). Every value comes from the same public resolver and cell the
 * table draws, so the two presentations cannot disagree about a figure, and the bands follow the one column
 * catalog: a column added to the table appears here on the same deploy.
 *
 * A band with no reading is dropped: a strip of dashes reads as broken data on a phone, where the table's own
 * column head is not there to explain the gap. Tapping the card opens the row, exactly as the table's name does.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { Card } from '@uniquedomains/components/kit/card/Card';
import { Glyph } from '@uniquedomains/components/kit/glyph/Glyph';
import { Name } from '@uniquedomains/components/kit/name/Name';
import { Rating } from '@uniquedomains/components/kit/rating/Rating';
import { pickCardSeed } from '@/client/catalogs/landing/domains/cardVisuals';
import { PUBLIC_PREVIEW_TABLE_COPY, fillPreviewCopy } from '@/client/catalogs/landing/domains/publicPreviewCopy';

import PublicDomainsTableCell from '../table/PublicDomainsTableCell';
import { resolvePublicCellValue, resolvePublicRating } from '../table/publicCellValues';
import { splitDomainLabel } from '../table/publicTableIdentity';

// Types
import type { PublicDomainsTableColumn } from '@/client/catalogs/landing/domains/publicTableColumns';
import type { TableCellRow } from '@uniquedomains/components/domains/table/cellValues';
import type { DomainsSearchPublicTableRow } from '@uniquedomains/components/contracts/domains/search';

/*----------------------------------
- TYPES
----------------------------------*/

type PublicDomainsRowCardProps = {
    row: TableCellRow;
    projection: DomainsSearchPublicTableRow | null;
    /** The columns the card shows as bands: every catalog column the card's first line does not already own. */
    bandColumns: readonly PublicDomainsTableColumn[];
    /** Receives the row's domain, as the table's name button does. */
    onOpen: (domain: string) => void;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

const PublicDomainsRowCard = ({ row, projection, bandColumns, onOpen }: PublicDomainsRowCardProps) => {
    const label = splitDomainLabel(row.domain);
    const status = resolvePublicCellValue('acquisition', row, projection);
    const price = resolvePublicCellValue('askPrice', row, projection);
    const renewal = resolvePublicCellValue('renewal', row, projection);
    const rating = resolvePublicRating(projection);

    const open = () => onOpen(row.domain);

    /*
     * The card is a container, its first line the button that opens the drawer, its bands a sibling strip. The strip
     * scrolls sideways past the screen edge, so a keyboard must reach it: it is a focusable group that arrow keys
     * scroll (Gaetan, 2026-10-28, review m-31 answer A, report decision 101), and a focusable element may not sit inside
     * a button (nested interactive content). A tap on the strip still opens the drawer, as a tap anywhere on the card
     * did; Enter on it does nothing, the button beside it opens. No visible change.
     */
    return (
        <Card as="div" class="dm-card">
            {/* No aria-label: the button's name is what it shows (the name, its status, its price). "Open <name>" held
                none of the rest of that visible text, which fails WCAG 2.5.3 (Lighthouse `label-content-name-mismatch`,
                batch 7 review M6, report decision 62); the button role already says it opens. */}
            <button type="button" class="dm-card__open" data-testid="domains-row-card" onClick={open}>
                <span class="dm-card__top">
                    <Glyph letter={label.name} seed={pickCardSeed(row.domain)} size="lg" />
                    <span class="dm-card__id">
                        <Name word={label.name} tld={label.tld.replace(/^\./, '') || undefined} />
                        <PublicDomainsTableCell value={status} status={row.status} />
                    </span>
                    <span class="dm-card__money">
                        <PublicDomainsTableCell value={price} />
                        {renewal.kind === 'text' ? <span class="dm-renews">{PUBLIC_PREVIEW_TABLE_COPY.renews} {renewal.value}</span> : null}
                    </span>
                </span>
            </button>
            <div
                class="dm-bands"
                role="group"
                tabIndex={0}
                aria-label={fillPreviewCopy(PUBLIC_PREVIEW_TABLE_COPY.bandsLabel, { domain: row.domain })}
                onClick={open}
            >
                {rating ? (
                    <span class="dm-band">
                        {PUBLIC_PREVIEW_TABLE_COPY.ratingBand}
                        <b><Rating band={rating.band} confidence={rating.confidence} title={undefined} /></b>
                    </span>
                ) : null}
                {bandColumns.map((column) => {
                    const value = resolvePublicCellValue(column.id, row, projection);
                    if (value.kind === 'empty') return null;
                    return (
                        <span key={column.id} class="dm-band">
                            {column.label}
                            <b><PublicDomainsTableCell value={value} /></b>
                        </span>
                    );
                })}
            </div>
        </Card>
    );
};

export default PublicDomainsRowCard;
